import { useMemo } from "react";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import PropTypes from "prop-types";
import moment from "moment";
import { ErrorMessage } from "formik";
import {
  commonInputStyles,
  labelStyles
} from "components/form_new/component-styles";
import { TextField } from "@mui/material";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const DateInputField = ({ label, field, form, ...rest }) => {
  const errorId = `${field.name}-error`;
  const hasError = form.errors[field.name] && form.touched[field.name];

  const value = useMemo(() => {
    if (!field.value) return null;
    if (moment.isMoment(field.value)) return field.value;
    const momentValue = moment(field.value);
    return momentValue.isValid() ? momentValue : null;
  }, [field.value]);

  const displayValue = field.value && moment.isMoment(field.value)
    ? field.value.format("YYYY-MM-DD")
    : field.value
      ? moment(field.value).format("YYYY-MM-DD")
      : "";

  const handleChange = (event) => {
    const dateValue = event.target.value;
    if (dateValue) {
      const momentValue = moment(dateValue);
      momentValue.toJSON = function toDateOnlyJson() {
        return this.format("YYYY-MM-DD");
      };
      form.setFieldValue(field.name, momentValue);
    } else {
      form.setFieldValue(field.name, undefined);
    }
  };

  if (isIOS) {
    return (
      <div style={{ width: "100%", margin: 0 }}>
        <span
          id={errorId}
          className="form-field-error"
          role="status"
          aria-live="polite"
          style={{
            display: "block",
            color: "red",
            fontSize: "0.875rem",
            marginTop: "4px"
          }}
        >
          {hasError ? <ErrorMessage name={field.name}/> : null}
        </span>

        <TextField
          type="date"
          label={label}
          value={displayValue}
          onChange={handleChange}
          onBlur={() => form.setFieldTouched(field.name, true)}
          error={hasError}
          sx={{
            ...commonInputStyles,
            width: "100%"
          }}
          InputLabelProps={{
            sx: labelStyles,
            shrink: true
          }}
          inputProps={{
            "aria-describedby": hasError ? errorId : undefined,
            'aria-invalid': hasError ? "true" : "false",
            // iOS VoiceOver requires role="textbox" to expose native date inputs
            role: "textbox"
          }}
          {...rest}
        />
      </div>
    );
  }

  return (
    <div style={{ width: "100%", margin: 0 }}>
      <span
        id={errorId}
        className="form-field-error"
        role="status"
        aria-live="polite"
        style={{ display: "block", color: "red", fontSize: "0.875rem", marginTop: "4px" }}
      >
          {hasError ? <ErrorMessage name={field.name}/> : null}
      </span>

      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DesktopDatePicker
          label={label}
          name={field.name}
          value={value}
          onChange={value => {
            if (value && value.isValid && value.isValid()) {
              // Store the exact Moment MUI emitted (see the `useMemo` above).
              // Override `toJSON` per-instance so JSON.stringify emits a
              // timezone-independent YYYY-MM-DD string on the wire, keeping
              // the S3DEV2-7521 fix without swapping the stored type.
              value.toJSON = function toDateOnlyJson() {
                return this.format("YYYY-MM-DD");
              };
              form.setFieldValue(field.name, value);
            } else {
              form.setFieldValue(field.name, undefined);
            }
          }}
          onClose={() => {
            // Mark field as touched when date picker closes (after user interaction)
            form.setFieldTouched(field.name, true);
          }}
          slotProps={{
            textField: {
              sx: {
                ...commonInputStyles,
                width: "100%"
              },
              InputLabelProps: {
                sx: labelStyles,
                shrink: true
              },
              error: hasError,
              onBlur: () => form.setFieldTouched(field.name, true),
              inputProps: {
                'aria-describedby': hasError ? errorId : undefined,
                'aria-invalid': hasError ? "true" : "false"
              }
            },
            field: {
              clearable: true,
              onClear: () => form.setFieldValue(field.name, undefined)
            },
            popper: {
              sx: {
                '& .MuiPickersCalendarHeader-label': {
                  overflow: 'visible',
                },
                '& .MuiDayCalendar-weekDayLabel': {
                  minWidth: '40px',
                  width: 'auto',
                },
                '& .MuiPickersDay-root': {
                  minWidth: '40px',
                  minHeight: '40px',
                  width: 'auto',
                  height: 'auto',
                },
              }
            },
          }}
          {...rest}
        />
      </LocalizationProvider>
    </div>
  );

};

DateInputField.propTypes = {
  label: PropTypes.string.isRequired,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onBlur: PropTypes.func
  }),
  form: PropTypes.shape({
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
    errors: PropTypes.object,
    touched: PropTypes.object
  }).isRequired
};

export default DateInputField;
